import jwtDecode from 'jwt-decode';
import { useDispatch, useSelector } from 'react-redux';
import React, { useDebugValue, useEffect, useMemo } from 'react';
import { LinearProgress, Typography } from '@material-ui/core';
import { createSelector } from 'reselect';
import * as R from 'ramda';
import { useAsync, useInterval, useUpdate } from 'react-use';
import { Trans, useTranslation } from 'react-i18next';

import { hardLogout } from 'actions/Login';
import { getProductsInShoppingCart } from 'reducers/ShoppingCart';
import {
  getCustomerRegistryToken,
  getCustomerRegistryUrl,
  getIsDefaultCustomer,
} from 'reducers/customerSearch';
import { getPayments } from 'reducers/Payments';
import { getSessionKey } from 'reducers/Login';
import { doClientRequest } from 'services/ErplyAPI/core/ErplyAPI2';
import { useConfirmation } from 'components/Confirmation';
import { getSetting } from 'reducers/configs/settings';

const TIME = {
  /** One hour */
  h: 3600e3 as const,
  /** One minute */
  m: 60e3 as const,
  /** One second */
  s: 1e3 as const,
};

type SessionExpirationConfig = {
  /** This many seconds before expiration, the POS should begin showing the session timer */
  timer: number;
  /** This many seconds before expiration, the POS should auto-logout if there is no active sale (customer selected / products in cart) */
  customerOrCart: number;
  /** This many seconds before expiration, the POS should auto-logout as soon as there aren't payments */
  paymentsOnly: number;
};
const getConfig = createSelector(
  () => null,
  (n): SessionExpirationConfig => ({
    timer: TIME.h * 3,
    customerOrCart: TIME.h * 2,
    paymentsOnly: TIME.h * 1,
  }),
);

/**
 * Given a sessionKey, checks its expiration and return true if the expiration is sufficiently far in the future
 * that no timer would be displayed yet
 */
export const checkSessionNotExpiring = sessionKey => async (
  dispatch,
  getState,
) => {
  const { timer } = getConfig(getState());
  return (
    doClientRequest({ request: 'getSessionKeyInfo', sessionKey })
      // Expiration timestamp
      .then(res => Number(res.records[0]?.expireUnixTime) * 1000)
      // Time until expiration
      .then(time => time - Date.now())
      // Remaining time is greater
      .then(delta => delta > timer)
      // Any error, presume sessionkey is expired
      .catch(() => false)
  );
};

const getHasNoCustomerOrCart = createSelector(
  state => 0 < getProductsInShoppingCart(state).length,
  state => getIsDefaultCustomer(state),
  (hasProds, isDefaultCustomer) => !hasProds && isDefaultCustomer,
);
const getHasNoPayments = createSelector(
  state => getPayments(state),
  pmts => R.isEmpty(pmts),
);

const getCustomerRegistryExpiration = () => {
  const customerRegistryUrl = getCustomerRegistryUrl();
  const customerRegistryToken = getCustomerRegistryToken();
  if (!customerRegistryUrl?.length || !customerRegistryToken?.length) {
    return -Infinity;
  }
  const decoded = jwtDecode(customerRegistryToken);
  return (decoded?.t_isst + decoded?.t_ttl) * 1000 || -Infinity;
};

/**
 * @param {number} buffer Buffer time in ms, how long before expiration we actually care about the timer
 * This will enable the component to only rerender every second if expiration is closer than that time
 */
const usePreExpirationLogoutTimer = (buffer: number) => {
  // region read session key info
  const sessionKey = useSelector(getSessionKey);
  const { value: sessionExpiresAt = 0, loading, error } = useAsync(
    () =>
      doClientRequest({ request: 'getSessionKeyInfo', sessionKey }).then(
        res => {
          return Number(res.records[0]?.expireUnixTime) * 1000;
        },
      ),
    [sessionKey],
  );
  // endregion

  // region read customer registry info
  const shouldHaveCustomerRegistry = useSelector(
    getSetting('use_standalone_customer_registry'),
  );
  const customerRegistryExpiresAt = shouldHaveCustomerRegistry
    ? getCustomerRegistryExpiration()
    : Infinity;
  // endregion

  const now = Date.now();
  const expiresAt = Math.min(sessionExpiresAt, customerRegistryExpiresAt);
  const remainingTime = expiresAt ? expiresAt - now : Infinity;

  // region Rerender component every second once we are in warning distance
  const msUntilBufferStart =
    expiresAt === 0 ? null : Math.max(remainingTime - buffer, 0);
  const msTillNextSecond = 1001 - (Date.now() % 1000);
  useInterval(useUpdate(), msUntilBufferStart || msTillNextSecond);
  // endregion

  const retVal = {
    loading,
    error,
    value: remainingTime,
  } as {
    loading: boolean;
    error: false | Error;
    value: number;
  };
  useDebugValue(retVal);
  return retVal;
};

const timer = a => {
  if (!Number.isFinite(a)) return '';
  return `${a < 0 ? '-' : ''}${
    new Date(Math.abs(a) + 500)
      .toISOString()
      .split('T')[1]
      .split('.')[0]
  }`;
};

export const useSessionExpiration = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('sessionExpiration');

  const config = useSelector(getConfig);
  const expiration = usePreExpirationLogoutTimer(config.timer);

  const noActiveSale = useSelector(getHasNoCustomerOrCart);
  const noUnsavedPayments = useSelector(getHasNoPayments);
  const autoLogout = useMemo(() => {
    if (expiration.loading) return false;
    if (expiration.error) return false;
    if (expiration.value < config.paymentsOnly) return noUnsavedPayments;
    if (expiration.value < config.customerOrCart) return noActiveSale;
    return false;
  }, [expiration, noActiveSale, noUnsavedPayments]);

  const timerTotal = expiration.value;
  const [phase, timerNext] = useMemo(() => {
    if (expiration.loading) return ['loading', Infinity];
    if (expiration.error) return ['error', Infinity];
    if (expiration.value < config.paymentsOnly)
      return ['payment', expiration.value];
    if (expiration.value < config.customerOrCart)
      return ['cart', expiration.value - config.paymentsOnly];
    if (expiration.value < config.timer)
      return ['timer', expiration.value - config.customerOrCart];
    return ['none', ''];
  }, [expiration, config]);

  const shortStatus = useMemo(() => {
    return (
      <Trans
        t={t}
        i18nKey="shortWarning"
        tOptions={{ context: phase }}
        defaults=" "
        values={{
          timerNext: timer(timerNext),
          timerTotal: timer(timerTotal),
        }}
        components={{
          secondary: <Typography color="secondary" />,
          error: <Typography color="error" />,
        }}
      />
    );
  }, [expiration, phase, timerNext, timerTotal]);

  const longStatus = useMemo(() => {
    return (
      <Trans
        i18nKey="longWarning"
        tOptions={{ context: phase }}
        defaults=" "
        t={t}
        values={{
          timerNext: timer(timerNext),
          timerTotal: timer(timerTotal),
        }}
        components={{
          secondary: <Typography color="secondary" />,
          error: <Typography color="error" />,
          Loading: <LinearProgress variant="indeterminate" />,
        }}
      />
    );
  }, [expiration, phase, timerNext, timerTotal]);

  const confirm = useConfirmation();
  useEffect(() => {
    if (autoLogout) {
      confirm({
        title: t('alert.title'),
        body: t('alert.body'),
        noReject: true,
      }).then(() => dispatch(hardLogout()));
    }
  }, [autoLogout]);

  return [shortStatus, longStatus];
};
